import { Typography as Text } from '@material-ui/core';

import { RiArrowDownLine, RiArrowUpLine, RiFlaskLine } from '@remixicon/react';

import {
  ConfirmationConfigGrid,
  ResourceConfirmationCard,
  type ToolConfirmationRenderProps,
  getNestedValue,
  isPlainObject,
  resolveInputBody,
  useResolveResourceName,
} from '@spotify/plugin-confidence-chat-react';

import {
  type ParsedMetric,
  type RawMetric,
  type Treatment,
  formatMDE,
  formatWeight,
  getDirectionText,
  parseMetric,
} from './formatters';

import styles from './CreateAbtestConfirmation.module.css';

const CONFIG_FIELDS = [
  { key: 'displayName', label: 'Name' },
  { key: 'abtest.flag', label: 'Flag' },
  { key: 'surfaces', label: 'Surfaces' },
  { key: 'metrics.entity', label: 'Entity' },
  { key: 'metrics.assignmentTable', label: 'Assignment table' },
];

const MetricCard = ({ metric }: { metric: ParsedMetric }) => {
  const resolve = useResolveResourceName();
  const directionText = getDirectionText(metric.direction, metric.isGuardrail);
  const mdeText = formatMDE(metric.mde);

  return (
    <div className={styles.metricCard}>
      <Text
        variant="body2"
        style={{
          fontWeight: 'bold',
        }}
      >
        {resolve(metric.metric)}
      </Text>
      <div className={styles.metricDetails}>
        {metric.direction === 'DECREASE' ? (
          <RiArrowDownLine size={14} className={styles.directionIcon} />
        ) : (
          <RiArrowUpLine size={14} className={styles.directionIcon} />
        )}
        <Text variant="body2" color="textSecondary">
          {directionText}
        </Text>
        {mdeText && (
          <Text variant="body2" color="textSecondary">
            {mdeText}
          </Text>
        )}
      </div>
    </div>
  );
};

export const CreateAbtestConfirmation = (
  props: ToolConfirmationRenderProps,
) => {
  const parsedInput = isPlainObject(props.input)
    ? resolveInputBody(props.input)
    : {};

  const treatments = (getNestedValue(parsedInput, 'abtest.treatments') ??
    []) as Treatment[];
  const rawMetrics = (getNestedValue(parsedInput, 'metrics.metrics') ??
    []) as RawMetric[];

  const parsedMetrics = rawMetrics.map(parseMetric);
  const successMetrics = parsedMetrics.filter(m => !m.isGuardrail);
  const guardrailMetrics = parsedMetrics.filter(m => m.isGuardrail);

  const metricResourceNames = parsedMetrics.map(m => m.metric).filter(Boolean);

  return (
    <ResourceConfirmationCard
      icon={<RiFlaskLine size={16} />}
      title="Create A/B test"
      resourceHints={metricResourceNames}
      {...props}
    >
      <ConfirmationConfigGrid
        input={parsedInput}
        previewFields={CONFIG_FIELDS}
      />
      {treatments.length > 0 && (
        <div className={styles.section}>
          <Text
            variant="body2"
            style={{
              fontWeight: 'bold',
            }}
          >
            Treatments
          </Text>
          <div className={styles.treatments}>
            {treatments.map((treatment, index) => {
              const label = index === 0 ? 'Control' : 'Treatment';
              const name =
                treatment.displayName || treatment.name || treatment.variant;
              return (
                <div key={treatment.variant} className={styles.treatmentCard}>
                  <div className={styles.treatmentInfo}>
                    <Text variant="body2" color="textSecondary">
                      {label}
                    </Text>
                    <Text
                      variant="body2"
                      style={{
                        fontWeight: 'bold',
                      }}
                    >
                      {name}
                    </Text>
                  </div>
                  <Text variant="body2" color="textSecondary">
                    {formatWeight(treatment.weight)}
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {(successMetrics.length > 0 || guardrailMetrics.length > 0) && (
        <div className={styles.section}>
          <Text
            variant="body2"
            style={{
              fontWeight: 'bold',
            }}
          >
            Metrics
          </Text>
          {successMetrics.length > 0 && (
            <div className={styles.section}>
              <Text variant="body2" color="textSecondary">
                Success
              </Text>
              {successMetrics.map(m => (
                <MetricCard key={m.metric} metric={m} />
              ))}
            </div>
          )}
          {guardrailMetrics.length > 0 && (
            <div className={styles.section}>
              <Text variant="body2" color="textSecondary">
                Guardrail
              </Text>
              {guardrailMetrics.map(m => (
                <MetricCard key={m.metric} metric={m} />
              ))}
            </div>
          )}
        </div>
      )}
    </ResourceConfirmationCard>
  );
};
